import { Injectable } from '@angular/core';
import {
  CanActivate,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  Router,
} from '@angular/router';
import { authService } from './auth.service';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard implements CanActivate {
  constructor(private authService: authService, private router: Router) {}

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean {
    const token = localStorage.getItem('accessToken');
    
    let isAuthenticated = false;
    if (token) {
      try {
        isAuthenticated = !this.authService.isTokenExpired(token);
      } catch (e) {
        isAuthenticated = false;
      }
    }
  
    // If token exists but expired → force logout
    if (token && !isAuthenticated) {
      this.authService.logout('expired');
      return false;
    }
    
    const userRole = this.authService.getUserRole();
    const path = next.routeConfig?.path;
    
    // Redirect logged-in users away from login pages
    if (this.shouldRedirectToHome(path, isAuthenticated)) {
      this.router.navigate(['/leads/leads-managment']);
      return false;
    }
  
    // Allow login/forget pages for non-authenticated users
    if ((path === 'login' || path === 'forget') && !isAuthenticated) {
      return true;
    }
    
    // Not authenticated → redirect to login
    if (!isAuthenticated) {
      this.router.navigate(['/authentication/login']);
      return false;
    }
    
    // Check role-based access
    if (this.hasRoleAccess(state.url, userRole)) {
      return true;
    } else {
      if (userRole === 'LEAD') {
        this.router.navigate(['/portal/notification']);
      } else {
        this.router.navigate(['/leads/leads-managment']);
      }
      return false;
    }
  }

  private shouldRedirectToHome(
    path: string | undefined,
    isAuthenticated: boolean
  ): boolean {
    return (path === 'login' || path === 'forget') && isAuthenticated;
  }

  private hasRoleAccess(url: string, role: string): boolean {
    // Routes only super admins should access
    const companySettingsRoute = '/settings/profile/company';
  
    // All admin-level routes
    const adminRoutes = ['/admissions', '/leads', '/lead-pipeline', '/activities'];
  
    // --- LEAD RESTRICTIONS ---
    if (role === 'LEAD') {
      for (const route of [...adminRoutes, companySettingsRoute]) {
        if (url.startsWith(route)) {
          return false;
        }
      }
      return true;
    }
  
    // --- USER RESTRICTIONS ---
    if (role === 'USER') {
      // user cannot access company settings
      if (url.startsWith(companySettingsRoute)) {
        return false;
      }
      
      // user CAN access admin routes
      return true;
    }
  
    // --- ADMIN: everything allowed ---
    return true;
  }
}